'use client';

import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { useTenant } from '@/contexts/TenantContext';

// Plan item #26: persist fiscal-year selection across reloads, scoped per
// tenant so switching tenants doesn't carry over a year the new tenant may
// not have data for. Falls back to current calendar year when nothing is
// stored.

interface FiscalYearContextValue {
  fiscalYear: number;
  setFiscalYear: (year: number) => void;
}

const FiscalYearContext = createContext<FiscalYearContextValue>({
  fiscalYear: new Date().getFullYear(),
  setFiscalYear: () => {},
});

const STORAGE_PREFIX = 'revpulse_fy:';

function readStoredYear(storageKey: string | null): number {
  const current = new Date().getFullYear();
  if (typeof window === 'undefined' || !storageKey) return current;
  try {
    const parsed = parseInt(localStorage.getItem(storageKey) ?? '', 10);
    return Number.isNaN(parsed) ? current : parsed;
  } catch {
    return current;
  }
}

export function FiscalYearProvider({ children }: { children: React.ReactNode }) {
  // Key off the resolved tenant from context (not storage): it changes in the
  // same render pass as a tenant switch, so the FY picked for tenant A never
  // leaks into tenant B's queries while storage catches up.
  const { tenantSlug } = useTenant();
  const storageKey = tenantSlug ? `${STORAGE_PREFIX}${tenantSlug}` : null;
  // Read the stored year in the same render the tenant resolves in, not in
  // an effect after it: the render that flips tenantSlug on also enables
  // every data query, and with an effect those queries first fired for the
  // current calendar year and then again for the stored one — a wasted
  // /dashboard/snapshot (~2s) whenever the saved FY wasn't this year. The
  // tree under AuthProviderWrapper renders client-side only, so reading
  // storage during render can't mismatch server HTML.
  const storedYear = useMemo(() => readStoredYear(storageKey), [storageKey]);
  const [picked, setPicked] = useState<{ key: string | null; year: number } | null>(null);
  const fiscalYear = picked && picked.key === storageKey ? picked.year : storedYear;

  const setFiscalYear = useCallback(
    (year: number) => {
      setPicked({ key: storageKey, year });
      if (typeof window !== 'undefined' && storageKey) {
        try {
          localStorage.setItem(storageKey, String(year));
        } catch {
          /* storage unavailable: the pick lives for this session only */
        }
      }
    },
    [storageKey],
  );

  const value = useMemo(() => ({ fiscalYear, setFiscalYear }), [fiscalYear, setFiscalYear]);

  return (
    <FiscalYearContext.Provider value={value}>
      {children}
    </FiscalYearContext.Provider>
  );
}

export function useFiscalYear() {
  return useContext(FiscalYearContext);
}
